import { track } from "@/lib/track";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PlanetMark } from "@/components/planet-mark";
import { GoogleSignInButton } from "@/components/google-sign-in-button";
import { AppleSignInButton } from "@/components/apple-sign-in-button";
import { validateInviteCode, consumeInviteCode } from "@/lib/invite.functions";
import { Eye, EyeOff, Ticket } from "lucide-react";

export const Route = createFileRoute("/login")({
  component: Login,
  head: () => ({
    meta: [
      { title: "Sign in to Pyng" },
      { name: "description", content: "Sign in to Pyng to see today's people and relationship opportunities." },
      { property: "og:title", content: "Sign in to Pyng" },
      { property: "og:description", content: "Sign in to Pyng to see today's people and relationship opportunities." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function Login() {
  const { confirmAuth, user, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const validateCode = useServerFn(validateInviteCode);
  const consumeCode = useServerFn(consumeInviteCode);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<"signin" | "signup">("signin");

  // Already signed in? There's nothing to do here.
  useEffect(() => {
    if (!loading && user) navigate({ to: "/today", replace: true });
  }, [user, loading, navigate]);

  // Invite links land here as /login?code=PYNG-XXXX — prefill and flip to signup.
  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get("code");
    if (c) {
      setInviteCode(c.toUpperCase());
      setMode("signup");
      // Survive an OAuth round-trip so the waitlist screen can auto-fill it.
      sessionStorage.setItem("pyng:invite-code", c.toUpperCase());
    }
  }, []);

  const onGoogleTokens = async () => {
    const signedInUser = await confirmAuth();
    setBusy(false);
    if (!signedInUser) {
      toast.error("Google sign-in completed, but no session was created");
      return;
    }
    sessionStorage.removeItem("orbit:post-auth-path");
    await navigate({ to: "/today", replace: true });
  };

  const onGoogleError = (message: string) => {
    sessionStorage.removeItem("orbit:post-auth-path");
    setBusy(false);
    toast.error(message);
  };

  const onAppleTokens = async () => {
    const signedInUser = await confirmAuth();
    setBusy(false);
    if (!signedInUser) {
      toast.error("Apple sign-in completed, but no session was created");
      return;
    }
    sessionStorage.removeItem("orbit:post-auth-path");
    await navigate({ to: "/today", replace: true });
  };

  const onAppleError = (message: string) => {
    sessionStorage.removeItem("orbit:post-auth-path");
    setBusy(false);
    toast.error(message);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);

    if (mode === "signup") {
      if (!inviteCode.trim()) {
        toast.error("An invite code is required to create an account");
        setBusy(false);
        return;
      }
      const check = await validateCode({ data: { code: inviteCode.trim() } });
      if (!check.valid) {
        toast.error(check.reason ?? "That invite code isn't valid");
        setBusy(false);
        return;
      }
    }

    const { error } =
      mode === "signin"
        ? await supabase.auth.signInWithPassword({ email, password })
        : await supabase.auth.signUp({ email, password, options: { emailRedirectTo: window.location.origin + "/today" } });
    if (error) {
      toast.error(error.message);
      setBusy(false);
      return;
    }
    const signedInUser = await confirmAuth();
    if (!signedInUser) {
      setBusy(false);
      toast.error(
        mode === "signin"
          ? "Sign-in completed, but no session was created"
          : "Account created, but no session was started. Try signing in.",
      );
      return;
    }

    if (mode === "signup" && inviteCode.trim()) {
      try {
        const res: any = await consumeCode({ data: { code: inviteCode.trim() } });
        if (!res?.ok) {
          toast.error(res?.reason ?? "That invite code couldn't be redeemed");
          await qc.invalidateQueries();
          setBusy(false);
          await navigate({ to: "/welcome", replace: true });
          return;
        }
      } catch (err: any) {
        toast.error(err?.message ?? "Your account was created, but the code didn't redeem");
        await qc.invalidateQueries();
        setBusy(false);
        await navigate({ to: "/welcome", replace: true });
        return;
      }
      sessionStorage.removeItem("pyng:invite-code");
    }

    if (mode === "signup") {
      track("signup", { source: "login_page", method: "password" });
      toast.success("Welcome to Pyng.");
    }
    await qc.invalidateQueries();
    setBusy(false);
    await navigate({ to: "/today", replace: true });
  };

  return (
    <main className="relative mx-auto flex min-h-screen max-w-md items-center justify-center px-6">
      <Link
        to="/"
        className="absolute left-4 top-4 flex items-center gap-2 rounded-full px-2 py-1 text-foreground/90 hover:text-foreground sm:left-6 sm:top-6"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.25rem)" }}
        aria-label="Pyng home"
      >
        <PlanetMark size={28} />
        <span className="text-sm font-medium tracking-tight">Pyng</span>
      </Link>
      <div className="w-full glass rounded-3xl p-8 animate-in-fade">
        <h1 className="text-center text-2xl font-semibold tracking-tight">Welcome to Pyng</h1>
        <p className="mt-1 text-center text-sm text-muted-foreground">Sign in to see today's people.</p>

        <div className="mt-6 space-y-2.5">
          <GoogleSignInButton
            busy={busy}
            onStart={() => {
              setBusy(true);
              sessionStorage.setItem("orbit:post-auth-path", "/today");
            }}
            onTokens={onGoogleTokens}
            onError={onGoogleError}
          />
          <AppleSignInButton
            busy={busy}
            onStart={() => {
              setBusy(true);
              sessionStorage.setItem("orbit:post-auth-path", "/today");
            }}
            onTokens={onAppleTokens}
            onError={onAppleError}
          />
        </div>

        <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
          <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 w-full rounded-xl border border-border bg-card px-4 text-sm outline-none ring-0 transition-colors focus:border-[var(--accent-neon)] focus:ring-2 focus:ring-[color-mix(in_oklab,var(--accent-neon)_28%,transparent)] placeholder:text-muted-foreground" />
          <div className="relative">
            <input type={showPassword ? "text" : "password"} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="h-11 w-full rounded-xl border border-border bg-card px-4 pr-11 text-sm outline-none transition-colors focus:border-[var(--accent-neon)] focus:ring-2 focus:ring-[color-mix(in_oklab,var(--accent-neon)_28%,transparent)] placeholder:text-muted-foreground" />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {mode === "signup" && (
            <div className="relative animate-in-fade">
              <Ticket className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Invite code"
                className="h-11 w-full rounded-xl border border-border bg-card px-4 pl-10 text-sm uppercase tracking-wider outline-none transition-colors focus:border-[var(--accent-neon)] focus:ring-2 focus:ring-[color-mix(in_oklab,var(--accent-neon)_28%,transparent)] placeholder:text-muted-foreground placeholder:normal-case placeholder:tracking-normal"
                autoComplete="off"
              />
            </div>
          )}
          <button disabled={busy} className="h-11 w-full rounded-xl bg-primary text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-px disabled:opacity-60 hover:shadow-[0_8px_24px_-8px_color-mix(in_oklab,var(--accent-neon)_70%,transparent)]">
            {mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>
        <button onClick={() => setMode(mode === "signin" ? "signup" : "signin")} className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground">
          {mode === "signin" ? "Need an account? Sign up" : "Already have an account? Sign in"}
        </button>
      </div>
    </main>
  );
}
